import React, {FC, useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {useFormik} from 'formik'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Select, {MultiValue} from 'react-select'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {loadLocations} from '../location/Api'
import {loadQuestions} from '../question/Api'
import {loadHrProcesses} from '../process/Api'
import {ILocationModel} from '../location/LocationModel'
import {getInputTypeLabel, IQuestionModel} from '../question/QuestionModel'
import {IHrProcessModel} from '../process/HrProcessModel'
import {
  EMPLOYMENT_TYPE_OPTIONS,
  IJobFormValues,
  IJobModel,
  initialJobForm,
  SECTOR_OPTIONS,
  STATUS_OPTIONS,
  toJobFormValues,
  validationJob,
  WORK_TYPE_OPTIONS,
} from './JobModel'

/** Seçim listeleri tek seferde yüklenir; tanım ekranlarındaki kayıt sayısı bunun altında kalır */
const OPTION_PAGE_SIZE = 1000

const EDITOR_MODULES = {
  toolbar: [
    [{header: [2, 3, false]}],
    ['bold', 'italic', 'underline'],
    [{list: 'ordered'}, {list: 'bullet'}],
    [{align: []}],
    ['link'],
    ['clean'],
  ],
}

type SelectOption = {value: string; label: string}

type Props = {
  show: boolean
  editingItem: IJobModel | null
  loading: boolean
  onClose: () => void
  onSubmit: (values: IJobFormValues) => void
}

export const JobFormModal: FC<Props> = ({show, editingItem, loading, onClose, onSubmit}) => {
  const isEditing = !!editingItem
  const [locations, setLocations] = useState<ILocationModel[]>([])
  const [questions, setQuestions] = useState<IQuestionModel[]>([])
  const [processes, setProcesses] = useState<IHrProcessModel[]>([])
  const [optionsLoading, setOptionsLoading] = useState(false)

  const formik = useFormik<IJobFormValues>({
    initialValues: initialJobForm,
    validationSchema: validationJob,
    onSubmit,
  })

  // Modal her açıldığında formu yeni kayıt / düzenlenen kayıt ile doldur
  useEffect(() => {
    if (!show) return
    formik.resetForm({values: editingItem ? toJobFormValues(editingItem) : initialJobForm})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingItem])

  useEffect(() => {
    if (!show) return
    setOptionsLoading(true)
    Promise.all([
      loadLocations(0, OPTION_PAGE_SIZE),
      loadQuestions(0, OPTION_PAGE_SIZE),
      loadHrProcesses(0, OPTION_PAGE_SIZE),
    ])
      .then(([locationResponse, questionResponse, processResponse]) => {
        setLocations(locationResponse.data.payload.content)
        setQuestions(questionResponse.data.payload.content)
        setProcesses(processResponse.data.payload.content)
      })
      .catch(() => showErrorToast('Seçim listeleri yüklenirken hata oluştu'))
      .finally(() => setOptionsLoading(false))
  }, [show])

  const locationOptions = useMemo<SelectOption[]>(
    () =>
      locations.map((location) => ({
        value: location.id || '',
        label: `${location.province} / ${location.district}`,
      })),
    [locations]
  )

  const questionOptions = useMemo<SelectOption[]>(
    () =>
      questions.map((question) => ({
        value: question.id || '',
        label: `${question.label} (${getInputTypeLabel(question.inputType)})`,
      })),
    [questions]
  )

  const selectedOptions = (options: SelectOption[], ids: string[]) =>
    ids.map((id) => options.find((option) => option.value === id)).filter(Boolean) as SelectOption[]

  const setMultiValue = (field: 'locationIds' | 'questionIds') => (
    selected: MultiValue<SelectOption>
  ) => {
    formik.setFieldValue(
      field,
      selected.map((option) => option.value)
    )
    formik.setFieldTouched(field, true, false)
  }

  const fieldError = (field: keyof IJobFormValues) =>
    formik.touched[field] && formik.errors[field] ? (formik.errors[field] as string) : undefined

  const inputClass = (field: keyof IJobFormValues, base = 'form-control') =>
    `${base} ${fieldError(field) ? 'is-invalid' : ''}`

  return (
    <Modal
      show={show}
      onHide={onClose}
      size='xl'
      dialogClassName='modal-dialog-centered'
      backdrop='static'
      enforceFocus={false}
    >
      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='modal-header'>
          <h5 className='modal-title'>{isEditing ? 'İlan Düzenle' : 'Yeni İlan'}</h5>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>

        <div className='modal-body'>
          <div className='row g-5'>
            <div className='col-md-6'>
              <label className='form-label fw-bold required'>İlan Başlığı</label>
              <input
                type='text'
                autoFocus
                className={inputClass('title')}
                placeholder='Örn: Kıdemli Java Geliştirici'
                {...formik.getFieldProps('title')}
              />
              {fieldError('title') && <div className='invalid-feedback'>{fieldError('title')}</div>}
            </div>

            <div className='col-md-6'>
              <label className='form-label fw-bold'>Alt Başlık</label>
              <input
                type='text'
                className={inputClass('subTitle')}
                placeholder='Örn: Ödeme sistemleri ekibi'
                {...formik.getFieldProps('subTitle')}
              />
              {fieldError('subTitle') && (
                <div className='invalid-feedback'>{fieldError('subTitle')}</div>
              )}
            </div>

            <div className='col-md-3'>
              <label className='form-label fw-bold required'>Çalışma Şekli</label>
              <select className={inputClass('workType', 'form-select')} {...formik.getFieldProps('workType')}>
                <option value=''>Seçiniz</option>
                {WORK_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {fieldError('workType') && (
                <div className='invalid-feedback'>{fieldError('workType')}</div>
              )}
            </div>

            <div className='col-md-3'>
              <label className='form-label fw-bold required'>İstihdam Tipi</label>
              <select
                className={inputClass('employmentType', 'form-select')}
                {...formik.getFieldProps('employmentType')}
              >
                <option value=''>Seçiniz</option>
                {EMPLOYMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {fieldError('employmentType') && (
                <div className='invalid-feedback'>{fieldError('employmentType')}</div>
              )}
            </div>

            <div className='col-md-3'>
              <label className='form-label fw-bold'>Sektör</label>
              <select className='form-select' {...formik.getFieldProps('sector')}>
                <option value=''>Seçiniz</option>
                {SECTOR_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className='col-md-3'>
              <label className='form-label fw-bold required'>Durum</label>
              <select className='form-select' {...formik.getFieldProps('status')}>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className='col-md-6'>
              <label className='form-label fw-bold'>İşe Alım Süreci</label>
              <select className='form-select' {...formik.getFieldProps('hrProcessId')}>
                <option value=''>Seçiniz</option>
                {processes.map((process) => (
                  <option key={process.id || ''} value={process.id || ''}>
                    {process.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='col-md-6'>
              <label className='form-label fw-bold required'>Lokasyonlar</label>
              <Select<SelectOption, true>
                isMulti
                isLoading={optionsLoading}
                options={locationOptions}
                value={selectedOptions(locationOptions, formik.values.locationIds)}
                onChange={setMultiValue('locationIds')}
                placeholder='Lokasyon seçiniz'
                noOptionsMessage={() => 'Lokasyon bulunamadı'}
                classNamePrefix='react-select'
              />
              {fieldError('locationIds') && (
                <div className='text-danger fs-7 mt-1'>{fieldError('locationIds')}</div>
              )}
            </div>

            <div className='col-12'>
              <label className='form-label fw-bold'>Başvuru Soruları</label>
              <Select<SelectOption, true>
                isMulti
                isLoading={optionsLoading}
                options={questionOptions}
                value={selectedOptions(questionOptions, formik.values.questionIds)}
                onChange={setMultiValue('questionIds')}
                placeholder='Adaylara sorulacak soruları seçiniz'
                noOptionsMessage={() => 'Soru bulunamadı'}
                classNamePrefix='react-select'
              />
            </div>

            <div className='col-12'>
              <label className='form-label fw-bold required'>İlan Açıklaması</label>
              <ReactQuill
                theme='snow'
                modules={EDITOR_MODULES}
                value={formik.values.description}
                onChange={(html) => formik.setFieldValue('description', html)}
                onBlur={() => formik.setFieldTouched('description', true)}
                placeholder='Pozisyonun sorumluluklarını, aranan nitelikleri ve yan hakları yazınız'
              />
              {fieldError('description') && (
                <div className='text-danger fs-7 mt-1'>{fieldError('description')}</div>
              )}
            </div>
          </div>
        </div>

        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            İptal
          </button>
          <button type='submit' className='btn btn-primary' disabled={loading}>
            {loading ? (
              <span className='indicator-progress' style={{display: 'block'}}>
                Lütfen bekleyin...
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              </span>
            ) : isEditing ? (
              'Güncelle'
            ) : (
              'Kaydet'
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}
